import { makeAutoObservable } from "mobx";
import FirebaseService from "./FirebaseService";
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  orderBy,
  where,
  runTransaction,
  arrayRemove,
  arrayUnion,
} from "firebase/firestore";
import posthog from "posthog-js";

export type FeatureRequest = {
  id?: string;
  title: string;
  description: string;
  userId: string;
  userName: string;
  status: 'new' | 'planned' | 'in-progress' | 'completed' | 'declined';
  votes: number;
  votedBy: string[];
  createdAt: Date;
  updatedAt: Date;
  tags: string[];
  approved: boolean;
};

export type FeatureComment = {
  id?: string;
  featureRequestId: string;
  userId: string;
  userName: string;
  comment: string;
  createdAt: Date;
  approved: boolean;
};

export default class FeatureRequestService {
  private _firebaseService: FirebaseService;
  private _isLoadingFeatureRequests = false;
  private _featureRequests: FeatureRequest[] = [];

  get featureRequests() {
    return this._featureRequests;
  }

  get isLoadingFeatureRequests() {
    return this._isLoadingFeatureRequests;
  }

  constructor(firebaseService: FirebaseService) {
    this._firebaseService = firebaseService;
    makeAutoObservable(this, undefined, { autoBind: true, deep: false });
  }

  async loadFeatureRequests(userId?: string): Promise<void> {
    if (this._isLoadingFeatureRequests) {
      return;
    }

    this._isLoadingFeatureRequests = true;

    try {
      const db = this._firebaseService.db;
      const collectionRef = collection(db, 'featureRequests');

      // If userId is provided, get approved requests OR user's own requests
      const q = query(
        collectionRef,
        where('approved', '==', true),
        orderBy('votes', 'desc')
      );

      const snapshot = await getDocs(q);

      let requests = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        } as FeatureRequest;
      });

      // If userId is provided, also fetch the user's own unapproved requests
      if (userId) {
        const userRequestsQuery = query(
          collectionRef,
          where('userId', '==', userId),
          where('approved', '==', false)
        );

        const userSnapshot = await getDocs(userRequestsQuery);

        const userRequests = userSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate()
          } as FeatureRequest;
        });

        // Combine the requests and sort by votes
        requests = [...requests, ...userRequests].sort((a, b) => b.votes - a.votes);
      }

      this._featureRequests = requests;
      this._isLoadingFeatureRequests = false;
    } catch (error) {
      console.error('Error fetching feature requests:', error);
      posthog.capture('error', { source: 'loadFeatureRequests', error: (error as Error).message });
      this._isLoadingFeatureRequests = false;
      throw error;
    }
  }

  async createFeatureRequest(request: Omit<FeatureRequest, 'id' | 'votes' | 'votedBy' | 'createdAt' | 'updatedAt' | 'approved'>): Promise<FeatureRequest> {
    try {
      const db = this._firebaseService.db;
      const now = new Date();
      const newRequest = {
        ...request,
        votes: 0,
        votedBy: [],
        createdAt: now,
        updatedAt: now,
        approved: false, // New requests are not approved by default
      };

      const collectionRef = collection(db, 'featureRequests');
      const docRef = await addDoc(collectionRef, newRequest);

      const createdRequest = { id: docRef.id, ...newRequest } as FeatureRequest;

      posthog.capture('feature_request_created', {
        request_id: createdRequest.id,
        title: createdRequest.title
      });

      return createdRequest;
    } catch (error) {
      console.error('Error creating feature request:', error);
      posthog.capture('error', { source: 'createFeatureRequest', error: (error as Error).message });
      throw error;
    }
  }

  async updateFeatureRequestStatus(id: string, status: FeatureRequest['status']): Promise<void> {
    try {
      const db = this._firebaseService.db;
      const docRef = doc(db, 'featureRequests', id);

      await updateDoc(docRef, {
        status,
        updatedAt: new Date()
      });

      posthog.capture('feature_request_status_updated', {
        request_id: id,
        new_status: status
      });
    } catch (error) {
      console.error('Error updating feature request status:', error);
      posthog.capture('error', { source: 'updateFeatureRequestStatus', error: (error as Error).message });
      throw error;
    }
  }

  async voteForFeature(id: string, userId: string): Promise<void> {
    try {
      const db = this._firebaseService.db;

      // Use a transaction to handle concurrent votes safely
      await runTransaction(db, async (transaction) => {
        const docRef = doc(db, 'featureRequests', id);
        const docSnap = await transaction.get(docRef);

        if (!docSnap.exists()) {
          throw new Error('Feature request not found');
        }

        const data = docSnap.data() as FeatureRequest;
        const votedBy = data.votedBy || [];

        // If user already voted, remove their vote
        if (votedBy.includes(userId)) {
          transaction.update(docRef, {
            votes: data.votes - 1,
            votedBy: arrayRemove(userId),
            updatedAt: new Date()
          });
          posthog.capture('feature_request_unvoted', { request_id: id });
        } else {
          // Add user's vote
          transaction.update(docRef, {
            votes: data.votes + 1,
            votedBy: arrayUnion(userId),
            updatedAt: new Date()
          });
          posthog.capture('feature_request_voted', { request_id: id });
        }
      });

      // Update the local state immediately for UI responsiveness
      this.updateFeatureRequestAfterVote(id, userId);
    } catch (error) {
      console.error('Error voting for feature:', error);
      posthog.capture('error', { source: 'voteForFeature', error: (error as Error).message });
      throw error;
    }
  }

  async addComment(comment: Omit<FeatureComment, 'id' | 'createdAt' | 'approved'>): Promise<FeatureComment> {
    try {
      const db = this._firebaseService.db;
      const now = new Date();
      const newComment = {
        ...comment,
        createdAt: now,
        approved: false // Comments are not approved by default
      };

      const collectionRef = collection(db, 'featureComments');
      const docRef = await addDoc(collectionRef, newComment);

      const createdComment = { id: docRef.id, ...newComment } as FeatureComment;

      posthog.capture('feature_request_commented', {
        request_id: comment.featureRequestId
      });

      return createdComment;
    } catch (error) {
      console.error('Error adding comment:', error);
      posthog.capture('error', { source: 'addComment', error: (error as Error).message });
      throw error;
    }
  }

  async getCommentsForFeature(featureRequestId: string, userId?: string): Promise<FeatureComment[]> {
    try {
      const db = this._firebaseService.db;
      const commentsRef = collection(db, 'featureComments');

      // Get approved comments for this feature request
      const approvedCommentsQuery = query(
        commentsRef,
        where('featureRequestId', '==', featureRequestId),
        where('approved', '==', true),
        orderBy('createdAt', 'asc')
      );

      const approvedSnapshot = await getDocs(approvedCommentsQuery);
      let comments = approvedSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate()
        } as FeatureComment;
      });

      // If userId provided, also get the user's own unapproved comments
      if (userId) {
        const userCommentsQuery = query(
          commentsRef,
          where('featureRequestId', '==', featureRequestId),
          where('userId', '==', userId),
          where('approved', '==', false),
          orderBy('createdAt', 'asc')
        );

        const userSnapshot = await getDocs(userCommentsQuery);
        const userComments = userSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate()
          } as FeatureComment;
        });

        // Combine all comments and sort by createdAt
        comments = [...comments, ...userComments].sort((a, b) =>
          a.createdAt.getTime() - b.createdAt.getTime()
        );
      }

      return comments;
    } catch (error) {
      console.error('Error fetching feature comments:', error);
      posthog.capture('error', { source: 'getCommentsForFeature', error: (error as Error).message });
      throw error;
    }
  }

  addNewFeatureRequest(request: FeatureRequest): void {
    this._featureRequests = [request, ...this._featureRequests];
  }

  updateFeatureRequestAfterVote(id: string, userId: string): void {
    this._featureRequests = this._featureRequests.map(request => {
      if (request.id === id) {
        const hasVoted = request.votedBy.includes(userId);
        return {
          ...request,
          votes: hasVoted ? request.votes - 1 : request.votes + 1,
          votedBy: hasVoted
            ? request.votedBy.filter(uid => uid !== userId)
            : [...request.votedBy, userId]
        };
      }
      return request;
    });
  }

  async getCommentCountsForRequests(requestIds: string[]): Promise<{[key: string]: number}> {
    try {
      const db = this._firebaseService.db;
      const commentsRef = collection(db, 'featureComments');
      const counts: {[key: string]: number} = {};

      // Fetch comment counts for each request
      for (const requestId of requestIds) {
        const q = query(
          commentsRef,
          where('featureRequestId', '==', requestId),
          where('approved', '==', true)
        );

        const snapshot = await getDocs(q);
        counts[requestId] = snapshot.size;
      }

      return counts;
    } catch (error) {
      console.error('Error fetching comment counts:', error);
      posthog.capture('error', { source: 'getCommentCountsForRequests', error: (error as Error).message });
      return {};
    }
  }
}